 /**
 * Holds the state of layer activation
 * @module
 */

var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var Logger = require('../utils/logger');

/**
 * @constant
 * Sentinel key for layerStates with no pageId
 * @type {string}
 * @private
 */
var NO_VIEW_ID = '*';

module.exports = {
  initialize: function() {
    // mapping of layerId => pageId/NO_VIEW_ID => layerState
    // NO_VIEW_ID is a sentinel value for layerStates that do not have an associated pageId
    this.__state = {} as any;
    this.on(actionTypes.LOAD_PERSISTED_LAYER_STATES, this.__loadPersistedLayerStates);
    this.on(actionTypes.RECORD_LAYER_DECISION, this.__recordDecision);
    this.on(actionTypes.RECORD_LAYER_DECISION_EVENT_ID, this.__recordDecisionEventId);
  },

  /**
   * Get a single layerState for a layerId, optional pageId
   * If there are multiple layerStates for the layerId specified, pageId must be included
   * @param {string} layerId
   * @param {string} [pageId]
   * @returns {LayerState|undefined}
   * @throws {Error} If no pageId is specified and there is more than one layerState for the layerId
   */
  getLayerState: function(layerId:string, pageId?:string): LayerState|undefined {
    if (this.__state[layerId]) {
      var layerStates = this.__state[layerId];
      if (_.keys(layerStates).length > 1 && !pageId) {
        throw new Error('View Id must be specified when more than one layerState for layer.');
      }

      if (pageId) {
        // If there is more than one layerState for a pageId (there shouldn't be), this will only return the first one
        return _.cloneDeep(_.find(layerStates, {pageId: pageId}));
      }

      return _.cloneDeep(layerStates[NO_VIEW_ID]);
    }
  },

  /**
   * Gets all layer states
   * @param {string} [namespace] String representing where current client will write data.
   * @return {LayerState[]}
   */
  getLayerStates: function(namespace?:string): LayerState[] {
    var layerStates = [];
    for (var layerId in this.__state) {
      _.forEach(this.__state[layerId], function(layerState) {
        if(_.isUndefined(namespace) || layerState.namespace === namespace) {
          layerStates.push(_.cloneDeep(layerState));
        }
      });
    }
    return layerStates;
  },

  /**
   * (UNSAFE) Get all Layer States in the format required for Analytics
   *
   * This method does *not* clone the decision object, so receivers *may* use the reference to mutate the core state (CJS-2156)
   *
   * @return {LayerStateAnalytics[]}
   */
  getLayerStatesForAnalytics: function(): LayerStateAnalytics[] {
    var layerStates = [];
    for (var layerId in this.__state) {
      _.forEach(this.__state[layerId], function(layerState) {
        layerStates.push(_.pick(layerState, ['layerId', 'decision', 'decisionEventId']));
      });
    }
    return layerStates;
  },

  /**
   * @param {Object} payload
   * @param {LayerState[]} payload.layerStates
   * @param {boolean} [payload.merge]
   * @private
   */
  __loadPersistedLayerStates: function(payload:any) {
    if (!payload.merge) {
      this.__state = {} as any;
    }
    _.each(payload.layerStates, _.bind(function(layerState) {
      var layerId = layerState.layerId;
      // Upgrade legacy layer states
      if (!layerState.pageId) {
        layerState.pageId = layerState.viewId;
      }
      var pageId = layerState.pageId || NO_VIEW_ID;
      var existingLayerStates = this.__state[layerId];
      // TODO(tyler): freeze individual layer states to avoid repeated cloning overhead
      if (_.isUndefined(existingLayerStates)) {
        // No existing layer states for this layerId
        this.__state[layerId] = {} as any;
        this.__state[layerId][pageId] = layerState;

      } else {
        var matchingLayerState = existingLayerStates[pageId];
        if (!matchingLayerState || // No matching layer states with this pageId
            layerState.decisionTimestamp > (matchingLayerState.decisionTimestamp || 0)) { // Incoming layerState is more recent
          this.__state[layerId][pageId] = layerState;
        }
      }
    }, this));
    this.emitChange();
  },

  /**
   * @param {Object} payload
   * @param {string} payload.layerId
   * @param {Decision} payload.decision
   * @param {DecisionTicket} payload.decisionTicket
   * @param {string} payload.timestamp
   * @param {string} payload.sessionId
   * @param {string} payload.activationId
   * @param {string} payload.revision
   * @param {string} payload.namespace
   * @param {string} [payload.pageId]
   * @private
   */
  __recordDecision: function(payload:any) {
    // TODO invariant
    var decision = {
      layerId: payload.layerId,
      revision: payload.revision,
      namespace: payload.namespace,
      pageId: payload.pageId,

      decisionTicket: payload.decisionTicket,


      decision: payload.decision,
      decisionActivationId: payload.activationId,

      decisionTimestamp: payload.timestamp,
      // holds the DecisionEventTicket id generated when we send a decision event to avro
      decisionEventId: null,
    };
    var layerStates = this.__state[payload.layerId] || {};

    // Single layer should never have both states with pageId and states with NO_VIEW_ID
    if (payload.pageId) {
      delete layerStates[NO_VIEW_ID];
      layerStates[payload.pageId] = decision;
    } else {
      layerStates = {} as any;
      layerStates[NO_VIEW_ID] = decision;
    }

    this.__state[payload.layerId] = layerStates;
    this.emitChange();
  },

  /**
   * Records the decision event id
   * @param {Object} payload
   * @param {string} payload.layerId
   * @param {string} payload.pageId
   * @param {string} payload.decisionId
   * @private
   */
  __recordDecisionEventId: function(payload:any) {
    var layerId = payload.layerId;
    var pageId = payload.pageId || NO_VIEW_ID;
    if (!this.__state[layerId]) {
      Logger.warn('Not recording decision event: Campaign not registered', layerId);
      return;
    }
    if (!this.__state[layerId][pageId]) {
      Logger.warn('Not recording decision event: Layer state not found for view', pageId);
      return;
    }
    this.__state[layerId][pageId].decisionEventId = payload.decisionId;
    this.emitChange();
  },
};
